"use client"

import { useState, useEffect, useCallback } from "react"
import { Skeleton, UnstyledButton } from "@forestsync/ui"
import { useDisclosure } from "@mantine/hooks"
import { useTranslations } from "next-intl"
import { TbWallet, TbCashPlus } from "react-icons/tb"
import { getTraderFundingInfoAction } from "@/actions/trade-payment"
import { DepositFundsModal } from "@/components/trading/DepositFundsModal"

export function TraderBalanceChip() {
  const t = useTranslations("nav")
  const [balanceCents, setBalanceCents] = useState<number | null>(null)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [fundingInstructions, setFundingInstructions] = useState<{
    iban: string
    bic: string
    accountHolder: string
    reference: string
  } | null>(null)
  const [opened, { open, close }] = useDisclosure(false)

  const fetchFundingInfo = useCallback(() => {
    setIsError(false)
    setBalanceCents(null)
    setIsLoading(true)
    getTraderFundingInfoAction()
      .then((res) => {
        if ("error" in res) { setIsError(true); return }
        setBalanceCents(res.balanceCents)
        setFundingInstructions(res.fundingInstructions)
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => { fetchFundingInfo() }, [fetchFundingInfo])


  if (!isError && balanceCents === null && !opened) return <Skeleton h={34} w={96} radius="md" />

  const balance = (balanceCents ?? 0) / 100
  const isZero = !isError && balance === 0

  const containerClass = [
    "group relative flex flex-row items-center gap-2 rounded-md px-3 h-[34px] cursor-pointer select-none",
    "transition-colors duration-200",
    isZero
      ? "bg-orange-500 hover:bg-orange-600"
      : isError
      ? "fs-hover-surface"
      : "hover:bg-teal-light",
  ].join(" ")

  const iconColor = isZero ? "text-white" : isError ? "text-gray-400 dark:text-gray-500" : "text-teal-600 dark:text-teal-400"
  const amountClass = ["text-xs font-semibold tabular-nums leading-none", isZero ? "text-white" : isError ? "text-gray-400 dark:text-gray-500" : "text-teal-600 dark:text-teal-400"].join(" ")
  const labelClass = ["text-[10px] font-medium leading-none", isZero ? "text-white/85" : isError ? "text-gray-400/60" : "text-teal-600/75 dark:text-teal-400/75"].join(" ")
  return (
    <>
      <UnstyledButton onClick={open} className={containerClass}>
        {/* Icon: wallet shoots up, cashplus rises in from below */}
        <span className={`relative shrink-0 w-[18px] h-[18px] overflow-hidden ${iconColor}`}>
          <TbWallet
            size={18}
            className="absolute inset-0 transition-all duration-180 ease-in group-hover:translate-y-[-120%] group-hover:opacity-0"
          />
          <TbCashPlus
            size={18}
            className="absolute inset-0 transition-all duration-180 ease-out translate-y-[120%] opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
          />
        </span>

        {/* flex-col: label expands via max-h, pushing amount up naturally */}
        <span className="flex flex-col items-start gap-[2px] leading-none">
          <span className={[
            "transition-all duration-200 ease-out group-hover:scale-[0.88]",
            amountClass,
          ].join(" ")}>
            {isError ? "–" : `€${balance.toFixed(2)}`}
          </span>
          <span className={[
            "max-h-0 overflow-hidden opacity-0 whitespace-nowrap",
            "transition-all duration-200 ease-out",
            "group-hover:max-h-[12px] group-hover:opacity-100",
            labelClass,
          ].join(" ")}>
            {t("addFunds")}
          </span>
        </span>
      </UnstyledButton>

      <DepositFundsModal
        opened={opened}
        onClose={close}
        fundingInstructions={fundingInstructions}
        onRetry={fetchFundingInfo}
        isRetrying={isLoading}
      />
    </>
  )
}
